/** Pulse Receiver Service */
import {Amplify} from "aws-amplify";
import {events} from "aws-amplify/data";

class PulseReceiverService{
    constructor(){
        this.url = "";
        this.region = "";
        this.token = "";
        this.isInitialized = false;
        this.channels = new Map();
        this.pendingChannels = new Map();
    }

    /**
     * Init service
     * @param {string} tenant - channel
     * @param {string} token - JWT
     * @param region
     * @param url
     * @param subtenant - optional subtenant to use in channel
     */
    init({tenant,token,region,url,subtenant}){
        if(this.isInitialized){
            throw new Error("PulseReceiverService is already initialized");
        }
        if(!tenant){
            throw new Error("tenant is required");
        }
        if(!token){
            throw new Error("token is required");
        }
        if(!region){
            throw new Error("region is required");
        }
        if(!url){
            throw new Error("url is required");
        }
        this.tenant = tenant;
        this.subtenant = subtenant?subtenant:"";
        this.region = region;
        this.url = url;
        this.token = token;
        Amplify.configure({
            API: {
                Events: {
                    endpoint: this.url,
                    region: this.region,
                    defaultAuthMode: "lambda",
                }
            }
        });
        this.isInitialized = true;
    }

    /**
     * Subscribe handler to topic, if channel doesn't exist, create it and subscribe to events, store handlers in entry, on event received, execute all handlers
     * @param module
     * @param action
     * @param handler
     * @returns {Promise<{unsubscribe: function(): void}>}
     */
    async onTopic(module,action,handler){
        if(!this.isInitialized){
            throw new Error("PulseReceiverService is not initialized");
        }
        if(!module){
            throw new Error("module is required");
        }
        if(!action){
            throw new Error("action is required");
        }
        if(typeof handler!=="function"){
            throw new Error("handler must be a function");
        }

        if(this.subtenant){ // prepend subtenant to module if defined
            module = `${this.subtenant}-${module}`;
        }

        const topic = `${this.tenant}/${module}/${action}`;
        let entry = this.channels.get(topic);
        if(!entry){
            let pending = this.pendingChannels.get(topic);

            if(!pending){
                pending = (async()=>{
                    const handlers = new Set();

                    try{
                        const channel = await events.connect(topic,{authMode:"lambda",authToken:this.token});
                        const subscription = channel.subscribe({
                            next: (data)=>{
                                for(const h of handlers){
                                    try{
                                        h(data?.event,data);
                                    }catch(err){
                                        console.error(`Handler error in topic ${topic}:`,err);
                                    }
                                }
                            },
                            error: (err)=>{
                                console.error(`Cannot subscribe to channel ${topic}:`,err);
                            }
                        },{authMode:"lambda",authToken:this.token});

                        const createdEntry = {channel,subscription,handlers};
                        this.channels.set(topic,createdEntry);
                        return createdEntry;
                    }finally{
                        this.pendingChannels.delete(topic);
                    }
                })();

                this.pendingChannels.set(topic,pending);
            }

            entry = await pending;
        }

        entry.handlers.add(handler);

        return {
            unsubscribe: ()=>{
                this.offTopic(module,action,handler);
            }
        };
    }

    /**
     * Unsubscribe handler from topic, if no handlers remain, close channel and remove entry
     * @param module
     * @param action
     * @param handler
     */
    offTopic(module,action,handler){
        if(!this.isInitialized){
            return;
        }
        if(this.subtenant){ // prepend subtenant to module if defined
            module = `${this.subtenant}-${module}`;
        }
        const topic = `${this.tenant}/${module}/${action}`;
        const entry = this.channels.get(topic);
        if(!entry){
            return;
        }
        entry.handlers.delete(handler);
        if(entry.handlers.size===0){
            entry.subscription?.unsubscribe();
            entry.channel?.close?.();
            this.channels.delete(topic);
        }
    }

    /**
     * Disconnect all channels and clear handlers
     */
    disconnectAll(){
        for(const [topic,entry] of this.channels.entries()){
            try{
                entry.subscription?.unsubscribe();
                entry.channel?.close?.();
            }catch(err){
                console.error(`Error closing topic ${topic}:`,err);
            }
        }

        this.channels.clear();
        this.pendingChannels.clear();
    }

    /**
     * Reset service to uninitialized state and disconnect all channels
     */
    reset(){
        this.disconnectAll();
        this.isInitialized = false;
        this.tenant = null;
        this.mode = "";
        this.region = "";
        this.url = "";
    }
}

const s_PulseReceiverService = new PulseReceiverService();
export {s_PulseReceiverService as PulseReceiverService};